import { type ReactNode } from "react";
import { Badge } from "./Badge";
import { ciRoleCan } from "../../shared/permissions";
import { authClient, type CiSession } from "../lib/auth-client";
import { Link, useRouter } from "../lib/router";

type LayoutProps = {
  /** Signed-in session */
  session: CiSession;
  /** Page */
  children: ReactNode;
};

type NavItem = {
  /** Path */
  to: string;
  /** Text */
  label: string;
  /** Shown as not built yet */
  soon?: boolean;
};

/** Top bar with navigation and the user menu. */
export const Layout = ({ session, children }: LayoutProps) => {
  const { pathname } = useRouter();
  const canManageUsers = ciRoleCan({
    role: session.user.role,
    permissions: { user: ["list"] },
  });
  const items: NavItem[] = [
    { to: "/mocks", label: "API mocks" },
    { to: "/test-results", label: "Test results", soon: true },
    { to: "/coverage", label: "Coverage", soon: true },
    ...(canManageUsers ? [{ to: "/users", label: "Users" }] : []),
  ];
  const signOut = async () => {
    await authClient.signOut();
    window.location.assign("/");
  };
  return (
    <div className="min-h-screen">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-x-6 gap-y-2 px-4 py-3">
          <Link
            to="/mocks"
            className="flex items-center gap-2 font-semibold text-slate-900"
          >
            <img src="/favicon.svg" alt="" className="h-6 w-6" />
            Pillar CI
          </Link>
          <nav className="flex flex-1 flex-wrap gap-1">
            {items.map((item) => {
              const active =
                pathname === item.to || pathname.startsWith(`${item.to}/`);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                    active
                      ? "bg-slate-900 text-white"
                      : "text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  {item.label}
                  {item.soon ? (
                    <span className="ml-1.5 text-[10px] uppercase tracking-wide opacity-60">
                      soon
                    </span>
                  ) : null}
                </Link>
              );
            })}
          </nav>
          <div className="flex items-center gap-3 text-sm">
            <span className="hidden text-slate-600 sm:inline">
              {session.user.name || session.user.email}
            </span>
            <Badge tone="violet">{session.user.role ?? "viewer"}</Badge>
            <button
              type="button"
              className="text-slate-500 hover:text-slate-900"
              onClick={signOut}
            >
              Sign out
            </button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-4 py-6">{children}</main>
    </div>
  );
};
